import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '@expressable/syntax-ds';
import { DocumentTitle } from 'components/DocumentTitle';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { rescheduleDirectFormSchema, RescheduleDirectFormData } from './schema';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getPartialRescheduleClientInformation, getReschedule } from 'domains/direct-reschedule/queries';
import { PartialRescheduleClientInformation, Reschedule as RescheduleData } from 'domains/direct-reschedule/types';
import { RescheduleDirectUi } from './ui';
import { SelectTherapistUi } from './blocks/SelectTherapistUi';
import RescheduleConfirmation from './blocks/RescheduleConfirmation';
import { useEvaluationCalendarSummary } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { useEvaluationCalendar } from 'domains/calendars/v3/useEvaluationCalendar';
import { useRescheduleInsurance } from './useRescheduleInsurance';
import { getRescheduleWindowDates } from './utils/getRescheduleWindowDates';
import { EvaluationDuration } from 'domains/calendars/types';
import { toStateAbbrev } from 'utils/states';
import { StateName } from 'utils/states';
import { get } from 'lodash';
import { useWatch } from 'react-hook-form';
import { findDefaultTimezoneOption } from 'domains/timezone';
import { DisplayTimezoneProvider } from 'domains/timezone/useDisplayTimezone';
import * as Sentry from '@sentry/react';
import { ContentContainer } from 'components/ContentContainer';
import { getTherapistEmailsByDateTime } from 'pages/ChangeInitialEvaluation/getTherapistEmailsByDateTime';
import { RESCHEDULE_DIRECT_MIXPANEL_EVENTS } from './utils/mixpanel';
import { useTrack } from 'utils/hooks/useMixPanel';

enum Step {
  SelectSlot = 'select-slot',
  SelectTherapist = 'select-therapist',
  Confirm = 'confirm',
}

const RescheduleWithData = ({
  rescheduleData,
  partialRescheduleClientInformation,
  clientId,
}: {
  rescheduleData: RescheduleData;
  partialRescheduleClientInformation: PartialRescheduleClientInformation;
  clientId: string;
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(Step.SelectSlot);
  const { track } = useTrack(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.ViewedRescheduleDirect);
  const methods = useForm<RescheduleDirectFormData>({
    resolver: zodResolver(rescheduleDirectFormSchema),
    defaultValues: {
      timeSlot: undefined,
      day: undefined,
    },
    mode: 'onChange',
  });

  const selectedCalendarId = useWatch({ control: methods.control, name: 'calendarId' });
  const selectedDay = useWatch({ control: methods.control, name: 'day' });
  const selectedTimeSlot = useWatch({ control: methods.control, name: 'timeSlot' });
  const showOnlyCurrentTherapist = useWatch({ control: methods.control, name: 'showOnlyCurrentTherapist' });

  // Fetch insurance data from Contentful for fallback logic
  const { data: rescheduleInsuranceData } = useRescheduleInsurance({
    insurancePayer: partialRescheduleClientInformation.insurancePayer,
    insuredState: partialRescheduleClientInformation.insuredState,
    insuranceNameContentful: partialRescheduleClientInformation.insuranceNameContentful,
  });

  // Build fallback insurance names from alternates
  const fallbackInsuranceNames = useMemo(() => {
    if (!rescheduleInsuranceData?.alternates || rescheduleInsuranceData.alternates.length === 0) {
      return undefined;
    }
    return rescheduleInsuranceData.alternates.map(alt => alt.fields.insuranceName);
  }, [rescheduleInsuranceData?.alternates]);

  // Get primary insurance name from Contentful (more reliable than partial info directly)
  const primaryInsuranceName = useMemo(() => {
    return rescheduleInsuranceData?.primary?.fields.insuranceName || partialRescheduleClientInformation.insuranceNameContentful;
  }, [rescheduleInsuranceData?.primary, partialRescheduleClientInformation.insuranceNameContentful]);

  const summaryQueryParams = useMemo(() => {
    // Calculate date window from original appointment date using the appointment's timezone
    const { fromDate, toDate } = getRescheduleWindowDates(
      rescheduleData.originalAcuityAppointment.datetime,
      rescheduleData.originalAcuityAppointment.timezone,
    );
    
    const params = {
      fromDate,
      toDate,
      duration: EvaluationDuration.Split,
      insuranceName: primaryInsuranceName,
      fallbackInsuranceNames,
      state: toStateAbbrev(partialRescheduleClientInformation.state as unknown as StateName) ?? undefined,
      detectedAreas: partialRescheduleClientInformation.detectedAreas,
    };

    // If client accepts reassignments, send primaryTherapistID with acceptReassignments
    if (partialRescheduleClientInformation.careTeam?.allowReassignment) {
      return {
        ...params,
        primaryTherapistID: partialRescheduleClientInformation.careTeam.primary.therapistEmail,
        acceptReassignments: true,
      };
    }

    // Only add therapistID filter if client is not allowed multiple therapists
    if (!partialRescheduleClientInformation.allowMultipleTreatingTherapists) {
      return {
        ...params,
        therapistID: partialRescheduleClientInformation.careTeam?.primary.therapistEmail,
        clientID: clientId,
      };
    }

    // If client is allowed multiple therapists, add acceptReassignments parameter
    return {
      ...params,
      acceptReassignments: true,
    };
  }, [rescheduleData, partialRescheduleClientInformation, clientId, primaryInsuranceName, fallbackInsuranceNames]);

  const summaryQuery = useEvaluationCalendarSummary(summaryQueryParams);
  const availability = useEvaluationCalendar({ query: summaryQuery });

  const selectedTherapist = availability.currentAvailability?.availableTherapists?.find(
    therapist => therapist.acuityCalendarID === selectedCalendarId,
  );

  const therapistToBeDisplayed = useMemo(() => {
    if (!selectedDay || !selectedTimeSlot) return [];
    
    const allTherapists = getTherapistEmailsByDateTime(availability, selectedTimeSlot);
    
    // If "View my therapist's availability only" is checked, filter to only show primary therapist
    if (showOnlyCurrentTherapist && partialRescheduleClientInformation.careTeam?.primary?.therapistEmail) {
      return allTherapists.filter(
        therapist => therapist.therapistEmail === partialRescheduleClientInformation.careTeam?.primary.therapistEmail
      );
    }
    
    return allTherapists;
  }, [availability, selectedDay, selectedTimeSlot, showOnlyCurrentTherapist, partialRescheduleClientInformation]);

  // Only validate therapist selection in multi-therapist mode
  if (!selectedTherapist && step === Step.Confirm && partialRescheduleClientInformation.allowMultipleTreatingTherapists) {
    Sentry.captureException(new Error('Bad state :: No therapist found for selected calendar ID'));
    throw new Error('Bad state :: No therapist found for selected calendar ID');
  }

  const contactTimezoneOption = rescheduleData.originalAcuityAppointment?.timezone
    ? findDefaultTimezoneOption(rescheduleData.originalAcuityAppointment.timezone)
    : undefined;

  return (
    <>
      <DocumentTitle>{t('reschedule.pageTitle')}</DocumentTitle>
      <DisplayTimezoneProvider defaultTimezone={contactTimezoneOption}>
        <FormProvider {...methods}>
          {step === Step.SelectSlot && (
            <RescheduleDirectUi
              rescheduleInfo={rescheduleData}
              clientInfoData={partialRescheduleClientInformation}
              availability={availability}
              isLoading={availability.isLoading || summaryQuery.isLoading}
              onConfirm={() => {
                // If multiple therapists not allowed, skip therapist selection and go to confirm
                if (!partialRescheduleClientInformation.allowMultipleTreatingTherapists) {
                  setStep(Step.Confirm);
                  track(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.PickedRescheduleDirectDate);
                } else {
                  setStep(Step.SelectTherapist);
                  track(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.PickedRescheduleDirectDate);
                }
              }}
            />
          )}
          {step === Step.SelectTherapist && (
            <SelectTherapistUi
              rescheduleInfo={rescheduleData}
              onConfirmStep={() => {
                setStep(Step.Confirm);
                track(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.PickedRescheduleDirectTherapist);
              }}
              onEditReservation={() => setStep(Step.SelectSlot)}
              therapists={therapistToBeDisplayed.map(therapist => ({
                therapistID: therapist.therapistEmail,
                firstName: therapist.therapistName.split(' ')[0] ?? '',
                lastName: therapist.therapistName.split(' ')[1] ?? '',
                therapistEmail: therapist.therapistEmail,
                acuityCalendarID: therapist.calendarID,
              }))}
              state={
                toStateAbbrev(get(partialRescheduleClientInformation, 'state') as unknown as StateName) ?? undefined
              }
              clinicalAreas={partialRescheduleClientInformation.detectedAreas ?? []}
              insuranceName={partialRescheduleClientInformation.insuranceNameContentful}
            />
          )}

          {step === Step.Confirm && (
            <RescheduleConfirmation
              selectedTherapist={
                selectedTherapist ? `${selectedTherapist.firstName} ${selectedTherapist.lastName}` : undefined
              }
              rescheduleInformation={rescheduleData}
              onEditReservation={() => {
                // If multiple therapists not allowed, go back to select slot
                if (!partialRescheduleClientInformation.allowMultipleTreatingTherapists) {
                  setStep(Step.SelectSlot);
                } else {
                  setStep(Step.SelectTherapist);
                }
              }}
            />
          )}
        </FormProvider>
      </DisplayTimezoneProvider>
    </>
  );
};

export const Reschedule = (): JSX.Element | null => {
  const { t } = useTranslation();
  const { clientId, rescheduleId } = useParams<{ clientId: string; rescheduleId: string }>();

  if (!clientId || !rescheduleId) {
    throw new Error(t('errors.invalidClientState'));
  }

  const rescheduleQuery = useQuery(['reschedule', clientId, rescheduleId], () => getReschedule(clientId, rescheduleId));
  const clientInfoQuery = useQuery(
    ['partial-client-info', clientId],
    () => getPartialRescheduleClientInformation(clientId),
    {
      enabled: rescheduleQuery.isSuccess && rescheduleQuery.data.status === 'active',
    },
  );

  if (rescheduleQuery.isLoading || clientInfoQuery.isLoading) {
    return (
      <>
        <DocumentTitle>{t('reschedule.pageTitle')}</DocumentTitle>
        <ContentContainer variant="full">
          <ContentContainer variant="centered-md" className="items-stretch">
            <LoadingSpinner />
          </ContentContainer>
        </ContentContainer>
      </>
    );
  }

  if (rescheduleQuery.isError || clientInfoQuery.isError) {
    return <Navigate to="/reschedule/unavailable" />;
  }

  if (rescheduleQuery.data?.status !== 'active') {
    return <Navigate to="/reschedule/expired" />;
  }

  if (!rescheduleQuery.data || !clientInfoQuery.data) {
    return <LoadingSpinner />;
  }

  return (
    <RescheduleWithData
      rescheduleData={rescheduleQuery.data}
      partialRescheduleClientInformation={clientInfoQuery.data}
      clientId={clientId}
    />
  );
};

export default Reschedule;
